/**
 * Trunnion AI - Workflow Diff Utility
 * Pure functions that compare two workflow definitions to produce a structured diff result.
 * Detects added/removed/changed nodes, edges, and metadata differences.
 */

import type { WorkflowDefinition, WorkflowNode, WorkflowEdge } from '@/types';

export interface WorkflowDiffResult {
  /** Node IDs present only in version B (added) */
  addedNodes: string[];
  /** Node IDs present only in version A (removed) */
  removedNodes: string[];
  /** Nodes present in both but with different data */
  changedNodes: Array<{ id: string; changes: string[] }>;
  /** Edge IDs present only in version B (added) */
  addedEdges: string[];
  /** Edge IDs present only in version A (removed) */
  removedEdges: string[];
  /** Top-level metadata fields that differ between versions */
  metadataChanges: Array<{ field: string; oldValue: string; newValue: string }>;
}

/**
 * Compare two workflow definitions and return a structured diff result.
 */
export function diffWorkflows(
  versionA: WorkflowDefinition,
  versionB: WorkflowDefinition,
): WorkflowDiffResult {
  const result: WorkflowDiffResult = {
    addedNodes: [],
    removedNodes: [],
    changedNodes: [],
    addedEdges: [],
    removedEdges: [],
    metadataChanges: [],
  };

  // --- Node diffing ---
  const nodesA = new Map<string, WorkflowNode>(
    versionA.nodes.map((n) => [n.id, n]),
  );
  const nodesB = new Map<string, WorkflowNode>(
    versionB.nodes.map((n) => [n.id, n]),
  );

  // Find added nodes (in B but not A)
  for (const id of nodesB.keys()) {
    if (!nodesA.has(id)) {
      result.addedNodes.push(id);
    }
  }

  // Find removed nodes (in A but not B)
  for (const id of nodesA.keys()) {
    if (!nodesB.has(id)) {
      result.removedNodes.push(id);
    }
  }

  // Find changed nodes (in both but with differences)
  for (const [id, nodeA] of nodesA) {
    const nodeB = nodesB.get(id);
    if (!nodeB) continue;

    const changes = diffNodes(nodeA, nodeB);
    if (changes.length > 0) {
      result.changedNodes.push({ id, changes });
    }
  }

  // --- Edge diffing ---
  const edgesA = new Map<string, WorkflowEdge>(
    versionA.edges.map((e) => [e.id, e]),
  );
  const edgesB = new Map<string, WorkflowEdge>(
    versionB.edges.map((e) => [e.id, e]),
  );

  for (const id of edgesB.keys()) {
    if (!edgesA.has(id)) {
      result.addedEdges.push(id);
    }
  }

  for (const id of edgesA.keys()) {
    if (!edgesB.has(id)) {
      result.removedEdges.push(id);
    }
  }

  // --- Metadata diffing ---
  const metaFields: Array<keyof WorkflowDefinition> = [
    'name',
    'description',
    'version',
    'classification',
    'isActive',
  ];

  for (const field of metaFields) {
    const oldVal = versionA[field];
    const newVal = versionB[field];

    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      result.metadataChanges.push({
        field,
        oldValue: String(oldVal ?? ''),
        newValue: String(newVal ?? ''),
      });
    }
  }

  // Compare tags
  const tagsA = (versionA.tags ?? []).sort().join(',');
  const tagsB = (versionB.tags ?? []).sort().join(',');
  if (tagsA !== tagsB) {
    result.metadataChanges.push({
      field: 'tags',
      oldValue: tagsA || '(none)',
      newValue: tagsB || '(none)',
    });
  }

  return result;
}

/**
 * Compare two nodes and return a list of human-readable change descriptions.
 */
function diffNodes(nodeA: WorkflowNode, nodeB: WorkflowNode): string[] {
  const changes: string[] = [];

  // Check type change
  if (nodeA.type !== nodeB.type) {
    changes.push(`Type changed from "${nodeA.type}" to "${nodeB.type}"`);
  }

  // Check position change (threshold: > 10px to avoid noise)
  const dx = Math.abs((nodeA.position?.x ?? 0) - (nodeB.position?.x ?? 0));
  const dy = Math.abs((nodeA.position?.y ?? 0) - (nodeB.position?.y ?? 0));
  if (dx > 10 || dy > 10) {
    changes.push('Position changed');
  }

  // Deep compare data, skipping position-related and React Flow internal fields
  const dataChanges = diffNodeData(nodeA.data, nodeB.data);
  changes.push(...dataChanges);

  return changes;
}

/**
 * Deep compare node data objects and return human-readable differences.
 */
function diffNodeData(
  dataA: Record<string, unknown>,
  dataB: Record<string, unknown>,
): string[] {
  const changes: string[] = [];
  const allKeys = new Set([...Object.keys(dataA), ...Object.keys(dataB)]);

  // Skip internal/volatile keys
  const skipKeys = new Set(['measured', 'selected', 'dragging', 'resizing']);

  for (const key of allKeys) {
    if (skipKeys.has(key)) continue;

    const valA = dataA[key];
    const valB = dataB[key];

    if (valA === undefined && valB !== undefined) {
      changes.push(`"${key}" added: ${formatValue(valB)}`);
    } else if (valA !== undefined && valB === undefined) {
      changes.push(`"${key}" removed (was: ${formatValue(valA)})`);
    } else if (JSON.stringify(valA) !== JSON.stringify(valB)) {
      changes.push(`"${key}" changed from ${formatValue(valA)} to ${formatValue(valB)}`);
    }
  }

  return changes;
}

/**
 * Format a value for display in the diff output.
 */
function formatValue(val: unknown): string {
  if (val === null || val === undefined) return '(empty)';
  if (typeof val === 'string') return `"${val}"`;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (Array.isArray(val)) return `[${val.length} items]`;
  if (typeof val === 'object') return `{${Object.keys(val).length} fields}`;
  return String(val);
}

/**
 * Generate a summary line from a diff result.
 */
export function diffSummary(diff: WorkflowDiffResult): string {
  const parts: string[] = [];

  if (diff.addedNodes.length > 0) {
    parts.push(`${diff.addedNodes.length} node${diff.addedNodes.length !== 1 ? 's' : ''} added`);
  }
  if (diff.removedNodes.length > 0) {
    parts.push(`${diff.removedNodes.length} node${diff.removedNodes.length !== 1 ? 's' : ''} removed`);
  }
  if (diff.changedNodes.length > 0) {
    parts.push(`${diff.changedNodes.length} node${diff.changedNodes.length !== 1 ? 's' : ''} changed`);
  }
  if (diff.addedEdges.length > 0) {
    parts.push(`${diff.addedEdges.length} edge${diff.addedEdges.length !== 1 ? 's' : ''} added`);
  }
  if (diff.removedEdges.length > 0) {
    parts.push(`${diff.removedEdges.length} edge${diff.removedEdges.length !== 1 ? 's' : ''} removed`);
  }
  if (diff.metadataChanges.length > 0) {
    parts.push(`${diff.metadataChanges.length} metadata change${diff.metadataChanges.length !== 1 ? 's' : ''}`);
  }

  return parts.length > 0 ? parts.join(', ') : 'No changes';
}
